import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Send, Loader2, Zap } from "lucide-react";
import { useAuth, api } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { appTexts } from "@/i18n/appTexts";
import { speak, listen, sttAvailable, stopSpeaking } from "@/lib/voice";

const ROBOT = "/robots/hero_robot.png";

const Waveform = ({ active }) => (
  <div className="flex items-end justify-center gap-1 h-9" data-testid="awakening-waveform">
    {[12, 24, 34, 20, 30, 16, 36, 22, 28, 14].map((h, i) => (
      <motion.span
        key={i}
        animate={active ? { height: [h * 0.4, h, h * 0.5, h * 0.9, h * 0.4] } : { height: 5 }}
        transition={{ duration: 1 + (i % 3) * 0.2, repeat: active ? Infinity : 0, ease: "easeInOut", delay: i * 0.06 }}
        className="w-1.5 rounded-full bg-gradient-to-t from-[#00F5FF] to-[#B026FF]"
        style={{ height: active ? h : 5 }}
      />
    ))}
  </div>
);

export default function Awakening() {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = appTexts[lang].awakening;
  const navigate = useNavigate();
  const [phase, setPhase] = useState("incoming"); // incoming | turn1 | turn2 | replying | turn3 | dissolve
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [goalText, setGoalText] = useState("");
  const [busy, setBusy] = useState(false);
  const transcriptRef = useRef([]);

  const avatarName = user?.avatar_name || "Unit-X";
  const firstName = (user?.name || "").split(" ")[0];

  const say = async (text) => {
    setSubtitle(text);
    transcriptRef.current.push(`${avatarName}: ${text}`);
    setSpeaking(true);
    const vp = user?.avatar_voice ? { pitch: user.avatar_voice.pitch, rate: user.avatar_voice.rate } : {};
    await speak(text, lang, vp);
    setSpeaking(false);
  };

  const acceptCall = async () => {
    setPhase("turn1");
    say(t.turn1.replace("{user}", firstName).replace("{avatar}", avatarName)).then(() => {});
    setTimeout(() => setPhase("turn2"), 1200); // input shows quickly, speech continues in background
  };

  const captureVoice = async () => {
    setListening(true);
    const heard = await listen(lang);
    setListening(false);
    if (heard) {
      setGoalText(heard);
      submitGoal(heard);
    }
  };

  const submitGoal = async (text) => {
    const goal = (text || goalText).trim();
    if (!goal) return;
    transcriptRef.current.push(`${firstName}: ${goal}`);
    setBusy(true);
    setPhase("replying");
    try {
      const { data } = await api.post("/neural/onboarding-reply", { text: goal, lang });
      await say(data.reply);
    } catch {
      await say(t.fallbackReply);
    } finally {
      setBusy(false);
    }
    await say(t.turn3);
    setPhase("turn3");
  };

  const finish = () => {
    if (phase !== "turn3") return;
    stopSpeaking();
    setPhase("dissolve");
    // fire-and-forget: NEVER block the transition on the network
    api.post("/neural/onboarding-complete").catch(() => {});
    setTimeout(() => navigate("/dashboard", { replace: true }), 1400);
    // failsafe: hard redirect if SPA navigation didn't happen for any reason
    setTimeout(() => {
      if (window.location.pathname !== "/dashboard") window.location.href = "/dashboard";
    }, 2800);
  };

  return (
    <div
      className="min-h-screen bg-[#03030A] text-white flex flex-col items-center justify-center px-6 relative overflow-hidden"
      onClick={finish}
      data-testid="awakening-page"
    >
      <div className="absolute top-[-15%] left-[-10%] w-[500px] h-[500px] bg-[#00F5FF] blur-[160px] opacity-15 rounded-full pointer-events-none" />
      <div className="absolute bottom-[-15%] right-[-10%] w-[500px] h-[500px] bg-[#B026FF] blur-[160px] opacity-15 rounded-full pointer-events-none" />

      <AnimatePresence mode="wait">
        {phase === "incoming" ? (
          <motion.div key="incoming" exit={{ opacity: 0, scale: 0.9 }} className="flex flex-col items-center text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#00F5FF] mb-8 animate-pulse" data-testid="incoming-label">
              {t.incoming}
            </p>
            {/* Sleeping egg */}
            <div className="relative flex items-center justify-center mb-10">
              <motion.span
                animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0.15, 0.5] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute w-56 h-64 rounded-[50%] border-2 border-[#00F5FF]/40 shadow-[0_0_60px_rgba(0,245,255,0.25)]"
              />
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="w-48 h-56 rounded-[50%] bg-gradient-to-b from-[#0F0F1A] to-[#141428] border border-[#00F5FF]/25 flex items-center justify-center overflow-hidden"
                data-testid="sleeping-egg"
              >
                <img src={ROBOT} alt="sleeping avatar" className="w-28 object-contain grayscale opacity-40" />
              </motion.div>
            </div>
            <button
              data-testid="activate-sync-btn"
              onClick={(e) => { e.stopPropagation(); acceptCall(); }}
              className="inline-flex items-center gap-3 px-9 py-5 rounded-full font-heading font-black text-sm uppercase tracking-[0.15em] bg-gradient-to-r from-[#00F5FF] to-[#B026FF] text-white shadow-[0_0_35px_rgba(0,245,255,0.5)] hover:shadow-[0_0_55px_rgba(0,245,255,0.8)] hover:-translate-y-1 transition-all animate-neon-pulse"
            >
              <Zap size={18} className="fill-white" />
              {t.accept}
            </button>
          </motion.div>
        ) : phase === "dissolve" ? (
          <motion.div
            key="dissolve"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-[#03030A] flex items-center justify-center"
            data-testid="matrix-dissolve"
          >
            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.1em" }}
              animate={{ opacity: 1, letterSpacing: "0.6em" }}
              transition={{ duration: 1.1 }}
              className="font-heading font-black text-lg text-[#00FFAA] uppercase"
            >
              {t.syncComplete}
            </motion.p>
          </motion.div>
        ) : (
          <motion.div key="awake" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center text-center max-w-lg w-full">
            {/* Awake robot */}
            <div className="relative flex items-center justify-center mb-4">
              <motion.span
                animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                className="absolute w-40 h-40 rounded-full border-2 border-[#00FFAA]/50"
              />
              <motion.img
                src={ROBOT}
                alt={avatarName}
                animate={{ y: [0, -8, 0], rotate: speaking ? [0, 2, -2, 0] : 0 }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="w-36 object-contain drop-shadow-[0_0_30px_rgba(0,255,170,0.5)]"
                data-testid="awake-robot"
              />
            </div>
            <p className="font-heading font-black text-lg mb-1">{avatarName}</p>
            <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#00FFAA] mb-4">{t.linked}</p>
            <Waveform active={speaking || listening} />

            {/* Subtitle */}
            <AnimatePresence mode="wait">
              <motion.p
                key={subtitle}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-white/85 leading-relaxed bg-white/5 border border-[#00F5FF]/20 rounded-2xl px-5 py-4 mt-5 min-h-[64px]"
                data-testid="awakening-subtitle"
              >
                {subtitle}
              </motion.p>
            </AnimatePresence>

            {/* Turn 2 input */}
            {phase === "turn2" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full mt-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-2">
                  <input
                    data-testid="goal-input"
                    value={goalText}
                    onChange={(e) => setGoalText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitGoal()}
                    placeholder={t.goalPlaceholder}
                    className="flex-1 bg-white/5 border border-[#00F5FF]/30 rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#00F5FF]"
                  />
                  {sttAvailable() && (
                    <button
                      data-testid="goal-mic-btn"
                      onClick={captureVoice}
                      className={`w-12 h-12 rounded-full flex items-center justify-center border transition-all shrink-0 ${
                        listening ? "bg-[#FF4444]/20 border-[#FF4444] text-[#FF4444] animate-pulse" : "bg-[#00F5FF]/10 border-[#00F5FF]/40 text-[#00F5FF]"
                      }`}
                    >
                      <Mic size={17} />
                    </button>
                  )}
                  <button
                    data-testid="goal-send-btn"
                    onClick={() => submitGoal()}
                    disabled={!goalText.trim() || busy}
                    className="w-12 h-12 rounded-full flex items-center justify-center bg-[#B026FF] shadow-[0_0_18px_rgba(176,38,255,0.5)] disabled:opacity-40 shrink-0"
                  >
                    {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              </motion.div>
            )}

            {phase === "turn3" && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00FFAA] mt-7 animate-pulse" data-testid="tap-to-finish">
                {t.tapToFinish}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
